import useCatalogAccess from "../useCatalogAccess";
import PendingCatalogRequests, { pendingClassificationName, usePendingCatalogRequests } from "../catalogApprovals/PendingCatalogRequests";
import { useCatalogPendingText } from "../catalogApprovals/catalogPendingMessages";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import { catalogRequestError, importInternalCodeError } from "../internalCatalogCodes";
import ItemCatalogThumbnail from "../ItemCatalogThumbnail";
import CorporateRecordBadge from "../CorporateRecordBadge";
import InventoryPolicyChip from "../InventoryPolicyChip";
import ItemRecipeUsage, { ItemRecipeUsageButton } from "../ItemRecipeUsage";
import { ItemAvailabilityStatus } from "../ItemAvailabilityControl";
import { managedByAdmin } from "../../utils/tenderManagement";
import useTenderContext from "../../hooks/useTenderContext";
import usePurchasePricePreviews, { fetchPurchasePriceBatch, purchasePriceLine, PURCHASE_PRICE_PAGE_SIZE } from "../../hooks/usePurchasePricePreviews";
import PurchasePricePreview, { PurchasePricePagination, purchasePriceText } from "../PurchasePricePreview";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { getUnitName, unitSearchText } from "../../utils/unitNames";
import { getTenderTranslator } from "../../pages/tenderMessages";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import { itemCatalogText } from "../itemCatalogMessages";
import "../ItemCatalog.css";
import "./ItemsTab.prices.css";
import AddItemPopup from "../popups/AddItemPopup";
import EditItemPopup from "../popups/EditItemPopup";
import ItemMergePopup from "../popups/ItemMergePopup";
import ItemsFamiliesImportPopup from "../ItemsFamiliesImportPopup";
import ItemExcelOptionsPopup from "../ItemExcelOptionsPopup";
import { buildItemImportWorkbook, parseItemImportWorkbook } from "../../utils/itemImportWorkbook";
import {
  buildSearchBlob,
  formatItemsFamiliesDate,
  getItemCategoryOptions,
  getItemInventoryPolicy,
  getItemHierarchy,
  getItemSupplierNames,
  inventoryPolicyHint,
  inventoryPolicyLabel,
  readItemsFamiliesUser,
} from "../itemsFamiliesUi";

function buildItemSearchBlob(item) {
  const hierarchy = getItemHierarchy(item);
  return buildSearchBlob([
    item?.code,
    item?.supplier_code,
    item?.name,
    hierarchy.family,
    hierarchy.subfamily,
    hierarchy.category,
    hierarchy.subcategory,
    unitSearchText(item?.unit),
    getItemSupplierNames(item).join(" "),
    item?.description,
  ]);
}

export default function ItemsTab({ businessUnitId }) {
  const { languageId } = usePmsTheme();
  const pendingText = useCatalogPendingText();
  const t = useCallback((text) => itemCatalogText(languageId, text), [languageId]);
  const tenderText = getTenderTranslator(languageId);
  const currentUser = readItemsFamiliesUser();
  const activeBusinessUnitId = businessUnitId || currentUser?.active_business_unit_id;
  const access = useCatalogAccess(activeBusinessUnitId);
  const editPermissions = ["purchasing.items.create", "purchasing.items.update"];
  const importPermissions = ["purchasing.items.create", "purchasing.items.import"];
  const exportPermissions = ["purchasing.items.view", "purchasing.items.export"];
  const historyPermissions = ["purchasing.items.view", "purchasing.stock.current"];
  const canViewItems = access.can("purchasing.items.view");
  const canViewRecipes = access.can("purchasing.recipes.view");
  const canCreateItem = access.can("purchasing.items.create");
  const canEditItem = access.can(editPermissions);
  const canImport = access.can(importPermissions);
  const canExport = access.can(exportPermissions);
  const canViewMerges = access.can(historyPermissions);
  const canViewTenders = access.can("purchasing.tenders.view");
  const isMobile = useIsMobile();
  const tenderContext = useTenderContext(businessUnitId || currentUser?.active_business_unit_id);
  const tenderBadge = (item) => {
    if (!tenderContext.has_tenders || !(tenderContext.item_ids || []).some((id) => Number(id) === Number(item.id))) return null;
    return canViewTenders
      ? <a className="ifm-tag" href={`/purchasing/items/tenders?business_unit_id=${encodeURIComponent(businessUnitId || currentUser?.active_business_unit_id)}&item_id=${encodeURIComponent(item.id)}`} title={tenderText("View this item’s tender prices and dates")}>{tenderText("Tendered")}</a>
      : <span className="ifm-tag">{tenderText("Tendered")}</span>;
  };
  const fileInputRef = useRef(null);
  const itemsRequestRef = useRef(0);
  const templateRequestRef = useRef(0);
  const importRequestRef = useRef(0);
  const uploadContextRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [pageState, setPageState] = useState({ context: "", page: 1 });
  const [catalogScope, setCatalogScope] = useState("");
  const [priceRefreshKey, setPriceRefreshKey] = useState(0);
  const [notice, setNotice] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [mergeDialog, setMergeDialog] = useState(null);
  const [recipeItemId, setRecipeItemId] = useState(null);
  const [importRows, setImportRows] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  const [isImportPopupOpen, setIsImportPopupOpen] = useState(false);
  const [importError, setImportError] = useState("");
  const [importing, setImporting] = useState(false);
  const [importBusinessUnitId, setImportBusinessUnitId] = useState(null);
  const [isExcelOptionsOpen, setIsExcelOptionsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const pendingCatalog = usePendingCatalogRequests({ entityType: "item", businessUnitId: activeBusinessUnitId,
    enabled: canViewItems && statusFilter === "pending", refreshKey: isAddPopupOpen });
  const businessUnitRef = useRef(activeBusinessUnitId);
  const inventoryPolicyScopeRef = useRef(0);
  businessUnitRef.current = activeBusinessUnitId;
  const openItemMerge = (mode) => {
    if (!access.can(mode === "merge" ? [...editPermissions, "purchasing.stock.current", "purchasing.items.merge"] : historyPermissions) || !editingItem || managedByAdmin(editingItem) || !activeBusinessUnitId) return;
    setMergeDialog({ item: editingItem, mode, businessUnitId: activeBusinessUnitId });
  };
  useEffect(() => {
    itemsRequestRef.current += 1;
    inventoryPolicyScopeRef.current += 1;
    setPendingRequest(null);
    setItems([]);
    setCatalogScope("");
    setNotice(null);
    setMergeDialog(null);
    setRecipeItemId(null);
    setIsEditPopupOpen(false);
    setEditingItem(null);
    templateRequestRef.current += 1;
    importRequestRef.current += 1;
    setIsExcelOptionsOpen(false);
    setExporting(false);
    setDownloadingTemplate(false);
    setTemplateError("");
    setIsImportPopupOpen(false);
    setImportRows([]);
    setImportFileName("");
    setImportBusinessUnitId(null);
    setImportError("");
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    return () => { templateRequestRef.current += 1; importRequestRef.current += 1; inventoryPolicyScopeRef.current += 1; };
  }, [activeBusinessUnitId, access.contextKey]);

  useEffect(() => {
    if (notice?.tone !== "success") return;
    const timer = window.setTimeout(() => setNotice(null), 1500);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const fetchItems = useCallback(
    async ({ quiet = false } = {}) => {
      if (!canViewItems) {
        setItems([]);
        setLoading(false);
        return;
      }

      if (!quiet) {
        setLoading(true);
      }
      const request = ++itemsRequestRef.current;
      const stamp = access.capture();
      const isCurrent = () => request === itemsRequestRef.current && businessUnitRef.current === activeBusinessUnitId && access.isCurrent(stamp, "purchasing.items.view");

      try {
        const params = new URLSearchParams({ status: "all", catalog: "1", include_thumbnail: "true" });
        if (canViewRecipes) params.set("include_recipe_usage", "true");
        const response = await axios.get(
          `/api/purchasing/items?${params}`,
          access.requestConfig("purchasing.items.view")
        );
        if (!isCurrent()) return;
        setItems(response.data || []);
        setCatalogScope(`${activeBusinessUnitId}:${access.contextKey}`);
        setPriceRefreshKey((value) => value + 1);
      } catch (requestError) {
        if (!isCurrent()) return;
        console.error("[ITEMS][LOAD][ERROR]", requestError);
        setNotice({
          tone: "error",
          message: "We could not load the item catalog for this business unit.",
        });
      } finally {
        if (!quiet && isCurrent()) {
          setLoading(false);
        }
      }
    },
    [activeBusinessUnitId, canViewItems, canViewRecipes, access]
  );

  useEffect(() => {
    fetchItems();
    return () => { itemsRequestRef.current += 1; };
  }, [fetchItems]);

  const updateInventoryPolicy = async (item, policy) => {
    if (!access.can(editPermissions) || Boolean(item.merged_into_item_id)) return false;
    const stamp = access.capture();
    const scope = inventoryPolicyScopeRef.current;
    const isCurrent = () => scope === inventoryPolicyScopeRef.current && businessUnitRef.current === activeBusinessUnitId && access.isCurrent(stamp, editPermissions);
    try {
      const response = managedByAdmin(item)
        ? await axios.patch(`/api/purchasing/items/${item.id}/inventory-policy`, { inventory_policy: policy }, access.requestConfig(editPermissions))
        : await axios.put(`/api/purchasing/items/${item.id}`, { inventory_policy: policy }, access.requestConfig(editPermissions));
      if (!isCurrent()) return false;
      if (isPendingCatalogCreation(response.data)) {
        setPendingRequest(response.data.request);
        return;
      }
      // The write endpoint returns the raw item, without its computed policy fields.
      setItems((current) => current.map((entry) => entry.id === item.id ? {
        ...entry, inventory_policy: policy, inventory_policy_effective: policy,
        inventory_policy_source_level: "item", inventory_policy_source_name: entry.name,
      } : entry));
    } catch (_error) {
      return false;
    }
  };

  const familyOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [...items.map((item) => getItemHierarchy(item).family),
            ...pendingCatalog.requests.map((request) => pendingClassificationName(request, "family"))]
            .filter((value) => value && value !== "-")
        )
      ).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" })),
    [items, pendingCatalog.requests]
  );

  const categoryOptions = useMemo(
    () => [...new Set([...getItemCategoryOptions(items, familyFilter), ...pendingCatalog.requests
      .filter((request) => familyFilter === "all" || pendingClassificationName(request, "family") === familyFilter)
      .map((request) => pendingClassificationName(request, "category")).filter(Boolean)])].sort((left, right) => left.localeCompare(right)),
    [familyFilter, items, pendingCatalog.requests]
  );

  useEffect(() => {
    if (statusFilter === "pending" || categoryFilter === "all" || categoryOptions.includes(categoryFilter)) return;
    setCategoryFilter("all");
  }, [categoryFilter, categoryOptions, statusFilter]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();

    return items.filter((item) => {
      if (statusFilter === "active" && item.is_active === false) return false;
      if (statusFilter === "inactive" && item.is_active !== false) return false;
      const hierarchy = getItemHierarchy(item);
      if (familyFilter !== "all" && hierarchy.family !== familyFilter) return false;
      if (categoryFilter !== "all" && hierarchy.category !== categoryFilter) return false;
      if (query && !buildItemSearchBlob(item).includes(query)) return false;
      return true;
    });
  }, [categoryFilter, familyFilter, items, search, statusFilter]);

  // Keep the loaded catalogue available to filters and exports, but price and
  // render only one page. A context change resets before requesting any prices.
  const pageContext = JSON.stringify([activeBusinessUnitId, access.contextKey, search, familyFilter, categoryFilter, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PURCHASE_PRICE_PAGE_SIZE));
  const page = Math.min(pageCount, pageState.context === pageContext ? pageState.page : 1);
  useEffect(() => {
    setPageState((current) => current.context !== pageContext ? { context: pageContext, page: 1 }
      : current.page > pageCount ? { ...current, page: pageCount } : current);
  }, [pageContext, pageCount]);
  const visibleItems = useMemo(() => filteredItems.slice((page - 1) * PURCHASE_PRICE_PAGE_SIZE, page * PURCHASE_PRICE_PAGE_SIZE), [filteredItems, page]);
  const pricePreviews = usePurchasePricePreviews({
    items: visibleItems,
    businessUnitId: activeBusinessUnitId,
    enabled: canViewItems && !loading && statusFilter !== "pending" && catalogScope === `${activeBusinessUnitId}:${access.contextKey}`,
    contextKey: access.contextKey,
    refreshKey: priceRefreshKey,
  });
  const priceText = purchasePriceText(languageId);
  const renderPrice = (item) => <PurchasePricePreview amountOnly item={item} languageId={languageId}
    price={pricePreviews.prices[item.id]} loading={pricePreviews.loading}
    error={pricePreviews.error} onRetry={pricePreviews.retry} />;

  const stats = useMemo(() => {
    const visibleFamilies = new Set();
    const visibleCategories = new Set();
    const visibleUnits = new Set();
    let itemsWithSuppliers = 0;
    let directConsumption = 0;

    filteredItems.forEach((item) => {
      const hierarchy = getItemHierarchy(item);
      if (hierarchy.family !== "-") visibleFamilies.add(hierarchy.family);
      if (hierarchy.category !== "-") visibleCategories.add(hierarchy.category);
      if (item?.unit?.name) visibleUnits.add(item.unit.name);
      if (getItemSupplierNames(item).length) itemsWithSuppliers += 1;
      if (getItemInventoryPolicy(item).policy === "direct_consumption") directConsumption += 1;
    });

    return {
      visible: filteredItems.length,
      families: visibleFamilies.size,
      categories: visibleCategories.size,
      supplierLinked: itemsWithSuppliers,
      units: visibleUnits.size,
      directConsumption,
    };
  }, [filteredItems]);

  const exportToExcel = async () => {
    if (exporting || !access.can(exportPermissions) || statusFilter === "pending") return;
    const stamp = access.capture();
    setExporting(true);
    try {
      const options = access.requestConfig(exportPermissions);
      const response = await axios.get("/api/purchasing/items?status=all&catalog=1&export=1", {
        ...options, params: { ...options.params, ...(canViewRecipes ? { include_recipe_usage: "true" } : {}) },
      });
      if (!access.isCurrent(stamp, exportPermissions)) return;
      const query = search.trim().toLowerCase();
      const exportItems = (response.data || []).filter((item) => {
        const hierarchy = getItemHierarchy(item);
        return !(statusFilter === "active" && item.is_active === false) && !(statusFilter === "inactive" && item.is_active !== false)
          && (familyFilter === "all" || hierarchy.family === familyFilter) && (categoryFilter === "all" || hierarchy.category === categoryFilter)
          && (!query || buildItemSearchBlob(item).includes(query));
      });
      if (!exportItems.length) {
        setNotice({
          tone: "info",
          message: "There are no item rows in the current slice to export.",
        });
        return;
      }

      const prices = [];
      const batchSize = 50; // Maximum lines accepted by the price preview endpoint.
      for (let start = 0; start < exportItems.length; start += batchSize) {
        const lines = exportItems.slice(start, start + batchSize).map((item) => purchasePriceLine(item));
        const batch = await fetchPurchasePriceBatch({ currency: "EUR", lines }, options);
        if (!access.isCurrent(stamp, exportPermissions)) return;
        prices.push(...batch);
      }

      const exportRows = exportItems.map((item, index) => {
        const hierarchy = getItemHierarchy(item);
        const price = prices[index];
        const hasPrice = price.source === "tender" || price.source === "last_purchase";
        return {
          Code: item?.code || "",
          SupplierCode: item?.supplier_code || "",
          Name: item?.name || "",
          ...(canViewRecipes ? { [t("Recipes")]: (item.recipe_usage || []).map((recipe) => recipe.name).join("; ") } : {}),
          [priceText.price]: hasPrice ? Number(price.unit_price) : price.source === "error" ? priceText.error : null,
          [priceText.currency]: hasPrice ? price.currency || "EUR" : null,
          Status: t(item.is_active === false ? "Inactive" : "Active"),
          Family: hierarchy.family,
          Subfamily: hierarchy.subfamily,
          Category: hierarchy.category,
          Subcategory: hierarchy.subcategory,
          InventoryPolicy: inventoryPolicyLabel(getItemInventoryPolicy(item).policy),
          InventoryPolicySource: inventoryPolicyHint(getItemInventoryPolicy(item)),
          Unit: getUnitName(item?.unit, languageId),
          PackSize: item?.pack_size || "",
          MinimumPurchaseQuantity: item?.minimum_purchase_quantity ?? "",
          PurchaseMultiple: item?.purchase_multiple ?? "",
          Suppliers: getItemSupplierNames(item).join(", "),
          UpdatedAt: item?.updated_at || "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const columns = Object.keys(exportRows[0]);
      worksheet["!cols"] = [];
      worksheet["!cols"][columns.indexOf(priceText.price)] = { wch: 20 };
      if (canViewRecipes) {
        worksheet["!cols"][columns.indexOf(t("Recipes"))] = { wch: 60 };
      }
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Items");
      XLSX.writeFile(workbook, "items_catalog.xlsx");

      setNotice({
        tone: "success",
        message: `${exportItems.length} item rows exported to Excel.`,
      });
    } catch (_error) {
      if (access.isCurrent(stamp, exportPermissions)) setNotice({ tone: "error", message: "Could not export this catalog. Please try again." });
    } finally {
      if (access.isCurrent(stamp, exportPermissions)) setExporting(false);
    }
  };

  const downloadImportTemplate = async () => {
    if (!access.can(importPermissions) || !activeBusinessUnitId || downloadingTemplate) return;
    const unitId = activeBusinessUnitId;
    const request = ++templateRequestRef.current;
    const stamp = access.capture();
    const isCurrent = () => request === templateRequestRef.current && String(businessUnitRef.current) === String(unitId) && access.isCurrent(stamp, importPermissions);
    setDownloadingTemplate(true);
    setTemplateError("");
    try {
      const response = await axios.get("/api/purchasing/items/import/template", access.requestConfig(importPermissions));
      if (!isCurrent()) return;
      if (String(response.data?.business_unit_id) !== String(unitId)) {
        setTemplateError("The business unit changed. Download a new template for the current business unit.");
        return;
      }
      XLSX.writeFile(buildItemImportWorkbook(response.data, languageId), `items_import_template_${unitId}.xlsx`);
      setIsExcelOptionsOpen(false);
    } catch (requestError) {
      if (isCurrent()) setTemplateError(catalogRequestError(requestError, "We could not download the item template. Please try again."));
    } finally {
      if (isCurrent()) setDownloadingTemplate(false);
    }
  };

  const chooseImportFile = () => {
    if (!access.can(importPermissions) || !activeBusinessUnitId) return;
    uploadContextRef.current = { businessUnitId: activeBusinessUnitId, request: importRequestRef.current, stamp: access.capture() };
    setIsExcelOptionsOpen(false);
    fileInputRef.current?.click();
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    const input = event.target;
    if (!file || !access.can(importPermissions) || !activeBusinessUnitId) return;
    const context = uploadContextRef.current;
    if (context && (!access.isCurrent(context.stamp, importPermissions) || context.request !== importRequestRef.current || String(context.businessUnitId) !== String(activeBusinessUnitId))) {
      input.value = "";
      return;
    }
    const unitId = activeBusinessUnitId;
    const request = ++importRequestRef.current;
    const stamp = access.capture();
    const isCurrent = () => request === importRequestRef.current && String(businessUnitRef.current) === String(unitId) && access.isCurrent(stamp, importPermissions);
    uploadContextRef.current = null;

    const reader = new FileReader();

    reader.onload = (loadEvent) => {
      if (!isCurrent()) return;
      try {
        const data = new Uint8Array(loadEvent.target.result);
        const workbook = XLSX.read(data, { type: "array", raw: true, cellDates: false });
        const normalizedRows = parseItemImportWorkbook(workbook, languageId);

        if (!normalizedRows.length) {
          setNotice({
            tone: "error",
            message: "The selected file did not contain recognizable item rows.",
          });
          input.value = "";
          return;
        }

        setImportRows(normalizedRows);
        setImportBusinessUnitId(unitId);
        setImportFileName(file.name);
        setImportError("");
        setIsImportPopupOpen(true);
      } catch (requestError) {
        console.error("[ITEMS][IMPORT][PARSE][ERROR]", requestError);
        setNotice({
          tone: "error",
          message: requestError?.message || "We could not read the selected file. Please verify the format and try again.",
        });
      } finally {
        input.value = "";
      }
    };

    reader.onerror = () => {
      if (!isCurrent()) return;
      setNotice({
        tone: "error",
        message: "We could not read the selected file. Please try again.",
      });
      input.value = "";
    };

    reader.readAsArrayBuffer(file);
  };

  const handleConfirmImport = async () => {
    if (!access.can(importPermissions) || !importRows.length || importing) return;
    if (!importBusinessUnitId || String(importBusinessUnitId) !== String(businessUnitRef.current)) {
      setImportError("The business unit changed. Select your file again before importing.");
      return;
    }

    const codeError = importInternalCodeError(importRows, items);
    if (codeError) { setImportError(codeError); return; }

    setImporting(true);
    setImportError("");
    const unitId = importBusinessUnitId;
    const request = ++importRequestRef.current;
    const stamp = access.capture();
    const isCurrent = () => request === importRequestRef.current && String(businessUnitRef.current) === String(unitId) && access.isCurrent(stamp, importPermissions);

    try {
      const response = await axios.post("/api/purchasing/items/import", {
        business_unit_id: unitId,
        data: importRows, language: languageId,
      }, access.requestConfig(importPermissions));
      if (!isCurrent()) return;

      setIsImportPopupOpen(false);
      setImportRows([]);
      setImportFileName("");
      setImportBusinessUnitId(null);
      await fetchItems({ quiet: true });
      if (!isCurrent()) return;
      setNotice({
        tone: "success",
        message: `${response.data.inserted || response.data.inserted_items || 0} items imported successfully. ${
          response.data.links_created || response.data.created_links || 0
        } supplier links created.`,
      });
    } catch (requestError) {
      if (!isCurrent()) return;
      console.error("[ITEMS][IMPORT][ERROR]", requestError);
      setImportError(
        catalogRequestError(requestError, "We could not import this item file.")
      );
    } finally {
      if (isCurrent()) setImporting(false);
    }
  };

  const handleItemAdded = async (newItem) => {
    await fetchItems({ quiet: true });
    setNotice({
      tone: "success",
      message: `${newItem?.name || "Item"} created successfully.`,
    });
  };

  const handleItemUpdated = async () => {
    await fetchItems({ quiet: true });
    setNotice({
      tone: "success",
      message: `${editingItem?.name || "Item"} updated successfully.`,
    });
  };

  const handleAvailabilityUpdated = (updatedItem) => {
    setItems((current) => current.map((item) => Number(item.id) === Number(updatedItem.id)
      ? { ...item, ...updatedItem } : item));
  };

  if (!canViewItems) {
    return (
      <section className="spx-card pu-catalog-items">
        <div className="spx-empty">{t("You do not have permission to access the item catalog.")}</div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="spx-card pu-catalog-items">
        <div className="spx-empty">{t("Loading item catalog...")}</div>
      </section>
    );
  }

  return (
    <>
      <section className="spx-card pu-catalog-items">
        <div className="spx-head">
          <div className="spx-headMain">
            <h2 className="spx-title">{t("Items")}</h2>
          </div>

          <div className="spx-actions">
            {canCreateItem ? (
              <button type="button" className="spx-btn spx-btn--primary" onClick={() => setIsAddPopupOpen(true)}>
                {t("+ Add Item")}
              </button>
            ) : null}
            {canImport && <button type="button" className="spx-btn" disabled={!activeBusinessUnitId} onClick={() => { setTemplateError(""); setIsExcelOptionsOpen(true); }}>{t("Excel options")}</button>}
            {canExport && <button type="button" className="spx-btn" disabled={exporting || statusFilter === "pending"} onClick={exportToExcel}>
              {exporting ? priceText.preparingExcel : t("Export Excel")}
            </button>}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={handleImportFile}
            />
          </div>
        </div>

        <div className="spx-toolbar">
          <div className="ifm-itemFilters">
            <label>{t("Search")}<input type="search" className="spx-search" value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Search by code, supplier code, item, family, category or supplier...")} /></label>
            <label>{t("Family")}<select className="spx-select" value={familyFilter}
              onChange={(event) => { setFamilyFilter(event.target.value); setCategoryFilter("all"); }}>
              <option value="all">{t("All families")}</option>
              {familyOptions.map((family) => <option key={family} value={family}>{family}</option>)}
            </select></label>
            <label>{t("Category")}<select className="spx-select" value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}>
              <option value="all">{t("All categories")}</option>
              {categoryOptions.map((category) => <option key={category} value={category}>{category}</option>)}
            </select></label>
            <button type="button" className="spx-btn" disabled={!search && familyFilter === "all" && categoryFilter === "all" && statusFilter === "active"}
              onClick={() => { setSearch(""); setFamilyFilter("all"); setCategoryFilter("all"); setStatusFilter("active"); }}>{t("Clear filters")}</button>
          </div>
          <div className="ifm-statusFilters" role="group" aria-label={t("Item status")}>
            {["active", "inactive", "all", "pending"].map((status) => <button type="button" key={status}
              className={`spx-btn ${statusFilter === status ? "ifm-statusFilter--active" : ""}`}
              aria-pressed={statusFilter === status} onClick={() => setStatusFilter(status)}>
              {status === "pending" ? pendingText.pending : t({ active: "Active", inactive: "Inactive", all: "All" }[status])}
            </button>)}
          </div>
        </div>

        {notice?.tone === "success" ? (
          <div className="ifm-successNoticeViewport">
            <div className="ifm-successNotice" role="status" aria-atomic="true">
              <span className="ifm-successNoticeIcon" aria-hidden="true">✓</span>
              <span>{t(notice.message)}</span>
            </div>
          </div>
        ) : notice ? <div className={`spx-banner spx-banner--${notice.tone}`}>{t(notice.message)}</div> : null}

        {statusFilter === "pending" ? <PendingCatalogRequests entityType="item" businessUnitId={activeBusinessUnitId} state={pendingCatalog}
          search={search} familyFilter={familyFilter} categoryFilter={categoryFilter} /> : <>
        <div className="ifm-itemSummary" aria-live="polite">
          <span>{t("Showing")} <strong>{filteredItems.length}</strong> {t("of")} <strong>{items.length}</strong> {t("items")}</span>
          <span>{t("Families")}: <strong>{stats.families}</strong></span>
          <span>{t("Categories")}: <strong>{stats.categories}</strong></span>
          <span>{t("Supplier-Linked")}: <strong>{stats.supplierLinked}</strong></span>
          <span>{t("Units Used")}: <strong>{stats.units}</strong></span>
          <span>{t("Direct Consumption")}: <strong>{stats.directConsumption}</strong></span>
        </div>

        {!filteredItems.length ? (
          <div className="spx-empty">{t("No items match the current search or filters.")}</div>
        ) : isMobile ? (
          <div className="spx-mobileList">
            {visibleItems.map((item) => {
              const hierarchy = getItemHierarchy(item);
              const supplierNames = getItemSupplierNames(item);
              const inventoryPolicy = getItemInventoryPolicy(item);

              return (
                <article key={item.id} className="spx-mobileCard">
                  <div className="spx-mobileTop">
                    <div>
                      <div className="spx-code">{item?.code || `ITEM-${item.id}`}</div>
                      {canEditItem ? (
                        <button
                          type="button"
                          className="spx-linkBtn"
                          disabled={Boolean(item.merged_into_item_id)}
                          onClick={() => {
                            setEditingItem(item);
                            setIsEditPopupOpen(true);
                          }}
                        >
                          {item?.name || "-"}
                        </button>
                      ) : (
                        <div className="spx-strong">{item?.name || "-"}</div>
                      )}
                      <div className="ifm-itemFlags"><CorporateRecordBadge record={item} localInventoryPolicy />{tenderBadge(item)}<ItemAvailabilityStatus is_active={item.is_active} />{canViewRecipes && item.recipe_usage?.length > 0 && <ItemRecipeUsageButton item={item} onOpen={setRecipeItemId} />}</div>
                    </div>
                    <div className="ifm-tag">{getUnitName(item?.unit, languageId) || t("No unit")}</div>
                  </div>

                  <div className="ifm-tagRow">
                    <span className="ifm-tag ifm-tag--accent">{hierarchy.family}</span>
                    <span className="ifm-tag">{hierarchy.category}</span>
                    {hierarchy.subcategory !== "-" ? <span className="ifm-tag">{hierarchy.subcategory}</span> : null}
                    <InventoryPolicyChip policy={inventoryPolicy.policy} name={item.name} editable={canEditItem && !Boolean(item.merged_into_item_id)} onChange={(value) => updateInventoryPolicy(item, value)} />
                  </div>

                  <div className="spx-mobileRows">
                    <div>
                      <div className="spx-mobileLabel">{priceText.price}</div>
                      <div className="spx-mobileValue">{renderPrice(item)}</div>
                    </div>
                    <div>
                      <div className="spx-mobileLabel">{t("Suppliers")}</div>
                      <div className="spx-mobileValue ifm-supplierCell">
                        <span>{supplierNames.length ? supplierNames.join(", ") : "-"}</span>
                        <span className="ifm-supplierReference" title={t("Supplier Code")}>
                          <span className="ifm-srOnly">{t("Supplier Code")}: </span>{item?.supplier_code || "-"}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="spx-mobileLabel">{t("Pack")}</div>
                      <div className="spx-mobileValue">{item?.pack_size || "-"}</div>
                    </div>
                    <div>
                      <div className="spx-mobileLabel">{t("Updated")}</div>
                      <div className="spx-mobileValue">{formatItemsFamiliesDate(item?.updated_at)}</div>
                    </div>
                  </div>

                  {canEditItem ? (
                    <div className="spx-mobileActions">
                      <button
                        type="button"
                        className="spx-inlineBtn"
                        disabled={Boolean(item.merged_into_item_id)}
                          onClick={() => {
                          setEditingItem(item);
                          setIsEditPopupOpen(true);
                        }}
                      >
                        {t("Edit")}
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : (
          <div className="spx-tableWrap" tabIndex={0} role="region" aria-label={t("Items")}>
            <table className="spx-table ifm-table--items ifm-table--items--priced">
              <colgroup>
                {["image", "code", "item", "price", "supplier", "family", "subfamily", "category", "subcategory", "policy", "unit", "pack", "updated", ...(canViewRecipes ? ["recipes"] : []), "status"].map((column) => (
                  <col key={column} className={`ifm-col--${column}`} />
                ))}
                {canEditItem ? <col className="ifm-col--action" /> : null}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="ifm-imageCell">{languageId === "es" ? "Imagen" : languageId === "ca" ? "Imatge" : "Image"}</th>
                  <th scope="col">{t("Code")}</th>
                  <th scope="col">{t("Item")}</th>
                  <th scope="col">{priceText.price}</th>
                  <th scope="col">{t("Suppliers")}<span className="ifm-headerDetail">{t("Supplier Code")}</span></th>
                  <th scope="col">{t("Family")}</th>
                  <th scope="col">{t("Subfamily")}</th>
                  <th scope="col">{t("Category")}</th>
                  <th scope="col">{t("Subcategory")}</th>
                  <th scope="col">{t("Inventory Policy")}</th>
                  <th scope="col">{t("Unit")}</th>
                  <th scope="col">{t("Pack")}</th>
                  <th scope="col">{t("Updated")}</th>
                  {canViewRecipes && <th scope="col" className="ifm-recipeCell">{t("Recipes")}</th>}
                  <th scope="col">{t("Status")}</th>
                  {canEditItem ? <th scope="col" className="ifm-actionCell">{t("Action")}</th> : null}
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => {
                  const hierarchy = getItemHierarchy(item);
                  const supplierNames = getItemSupplierNames(item);
                  const suppliersLabel = supplierNames.length ? supplierNames.join(", ") : "-";
                  const inventoryPolicy = getItemInventoryPolicy(item);

                  return (
                    <tr key={item.id}>
                      <td className="ifm-imageCell"><ItemCatalogThumbnail item={item} languageId={languageId} config={access.requestConfig("purchasing.items.view")} /></td>
                      <td className="ifm-codeCell" title={item?.code || `ITEM-${item.id}`}>
                        <span className="spx-code">{item?.code || `ITEM-${item.id}`}</span>
                      </td>
                      <td className="ifm-nameCell" title={item?.name || "-"}>
                        {canEditItem ? (
                          <button
                            type="button"
                            className="spx-linkBtn ifm-fullText"
                            disabled={Boolean(item.merged_into_item_id)}
                          onClick={() => {
                              setEditingItem(item);
                              setIsEditPopupOpen(true);
                            }}
                          >
                            {item?.name || "-"}
                          </button>
                        ) : (
                          <span className="spx-strong ifm-fullText">{item?.name || "-"}</span>
                        )}
                        <div className="ifm-itemFlags"><CorporateRecordBadge record={item} localInventoryPolicy />{tenderBadge(item)}</div>
                      </td>
                      <td className="ifm-purchasePriceCell">{renderPrice(item)}</td>
                      <td className="ifm-supplierTableCell">
                        <div className="ifm-supplierCell">
                          <span className="ifm-fullText" title={suppliersLabel}>{suppliersLabel}</span>
                          <span className="ifm-supplierReference" title={`${t("Supplier Code")}: ${item?.supplier_code || "-"}`}>
                            <span className="ifm-srOnly">{t("Supplier Code")}: </span>{item?.supplier_code || "-"}
                          </span>
                        </div>
                      </td>
                      {[hierarchy.family, hierarchy.subfamily, hierarchy.category, hierarchy.subcategory].map((value, index) => (
                        <td key={index} className="spx-muted"><span className="ifm-fullText" title={value}>{value}</span></td>
                      ))}
                      <td>
                        <InventoryPolicyChip policy={inventoryPolicy.policy} name={item.name} editable={canEditItem && !Boolean(item.merged_into_item_id)} onChange={(value) => updateInventoryPolicy(item, value)} />
                      </td>
                      <td className="spx-muted ifm-unitCell"><span className="ifm-fullText" title={getUnitName(item?.unit, languageId) || "-"}>{getUnitName(item?.unit, languageId) || "-"}</span></td>
                      <td className="spx-muted ifm-packCell">{item?.pack_size || "-"}</td>
                      <td className="spx-muted ifm-dateCell">{formatItemsFamiliesDate(item?.updated_at)}</td>
                      {canViewRecipes && <td className="ifm-recipeCell"><ItemRecipeUsageButton item={item} onOpen={setRecipeItemId} /></td>}
                      <td><ItemAvailabilityStatus is_active={item.is_active} /></td>
                      {canEditItem ? (
                        <td className="ifm-actionCell"><div className="ifm-itemActions">
                          <button
                            type="button"
                            className="spx-inlineBtn"
                            disabled={Boolean(item.merged_into_item_id)}
                          onClick={() => {
                              setEditingItem(item);
                              setIsEditPopupOpen(true);
                            }}
                          >
                            {t("Edit")}
                          </button>
                        </div></td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {filteredItems.length > 0 && <PurchasePricePagination page={page} pageCount={pageCount}
          onPageChange={(nextPage) => setPageState({ context: pageContext, page: nextPage })}
          languageId={languageId} total={filteredItems.length} />}
        </>}
      </section>

      <ItemRecipeUsage item={canViewRecipes ? items.find((item) => item.id === recipeItemId) : null} onClose={() => setRecipeItemId(null)} />

      {pendingRequest && <CatalogCreationPendingNotice request={pendingRequest} onClose={() => setPendingRequest(null)} />}

      <AddItemPopup
        existingRecords={items}
        isOpen={isAddPopupOpen}
        onClose={() => setIsAddPopupOpen(false)}
        onAdded={handleItemAdded}
      />

      <EditItemPopup
        existingRecords={items}
        isOpen={isEditPopupOpen}
        item={editingItem}
        onClose={() => {
          setIsEditPopupOpen(false);
          setEditingItem(null);
        }}
        onUpdated={handleItemUpdated}
        onAvailabilityUpdated={handleAvailabilityUpdated}
        onOpenMerge={canViewMerges && activeBusinessUnitId ? openItemMerge : undefined}
        mergeOpen={Boolean(mergeDialog) && canViewMerges}
      />

      {mergeDialog && canViewMerges && String(mergeDialog.businessUnitId) === String(activeBusinessUnitId) && <ItemMergePopup
        key={`${activeBusinessUnitId}:${mergeDialog.item.id}`}
        initialItem={mergeDialog.item}
        items={items}
        businessUnitId={activeBusinessUnitId}
        mode={mergeDialog.mode}
        onClose={() => setMergeDialog(null)}
        onChanged={() => {
          // A completed merge can retire the edited item or change its links.
          // Reopen its record from the refreshed catalog before further edits.
          setIsEditPopupOpen(false);
          setEditingItem(null);
          fetchItems({ quiet: true });
        }}
      />}

      <ItemExcelOptionsPopup isOpen={canImport && isExcelOptionsOpen} onClose={() => setIsExcelOptionsOpen(false)}
        onDownload={downloadImportTemplate} onUpload={chooseImportFile} downloading={downloadingTemplate} error={templateError} />

      <ItemsFamiliesImportPopup
        isOpen={canImport && isImportPopupOpen && String(importBusinessUnitId) === String(activeBusinessUnitId)}
        onClose={() => {
          if (importing) return;
          setIsImportPopupOpen(false);
          setImportRows([]);
          setImportFileName("");
          setImportBusinessUnitId(null);
          setImportError("");
        }}
        title="Import Items"
        subtitle="Use the IDs from the References sheet. Invalid values or incompatible classifications reject the entire file; no items are saved."
        fileName={importFileName}
        rows={importRows}
        columns={Object.keys(importRows[0] || {}).filter((key) => key !== "_row_number")}
        error={importError}
        isSubmitting={importing}
        confirmLabel="Confirm Item Import"
        onConfirm={handleConfirmImport}
      />
    </>
  );
}
